import { useEffect, useState } from 'react';
import { Search, RotateCcw, AlertTriangle, Inbox } from 'lucide-react';
import { api, timeAgo, formatDuration } from '../api.js';
import { UserChip } from '../components/UserChip.jsx';
import { useGuild } from './GuildLayout.jsx';

const ACTION_LABELS = {
  warn: 'Warn', timeout: 'Timeout', untimeout: 'Untimeout',
  kick: 'Kick', ban: 'Ban', unban: 'Unban',
  channel_mute: 'Channel mute', channel_unmute: 'Channel unmute',
  channel_jail: 'Channel jail', channel_unjail: 'Channel unjail',
  warning_deleted: 'Warning deleted',
};

const ACTION_COLOR = {
  warn: 'warn', timeout: 'warn', channel_mute: 'warn', channel_jail: 'danger',
  kick: 'danger', ban: 'danger',
  untimeout: 'success', unban: 'success', channel_unmute: 'success', channel_unjail: 'success',
  warning_deleted: '',
};

const EMPTY = {
  target: '', moderator: '', action: '', source: '',
  q: '', since: '', until: '',
};

function toEpoch(dateStr) {
  if (!dateStr) return undefined;
  return Math.floor(new Date(dateStr).getTime() / 1000);
}

export default function AuditSearch() {
  const { guildId } = useGuild();
  const [filters, setFilters] = useState(EMPTY);
  const [results, setResults] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actions, setActions] = useState([]);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    api.auditActions(guildId).then(d => setActions(d.actions));
    // Auto-load latest 100 entries on mount
    runSearch({});
  }, [guildId]);

  async function runSearch(extras) {
    setLoading(true);
    try {
      const payload = {
        ...filters, ...extras,
        since: toEpoch(filters.since),
        until: toEpoch(filters.until ? filters.until + 'T23:59:59' : null),
      };
      const r = await api.auditSearch(guildId, payload);
      setResults(r.results);
      setTruncated(r.truncated);
    } catch (err) {
      setToast({ type: 'error', msg: err.message });
      setTimeout(() => setToast(null), 2500);
    } finally {
      setLoading(false);
    }
  }

  function update(k, v) { setFilters(f => ({ ...f, [k]: v })); }
  function submit(e) { e.preventDefault(); runSearch({}); }
  function reset() { setFilters(EMPTY); setTimeout(() => runSearch({}), 0); }

  const anyFilter = Object.values(filters).some(v => v);

  return (
    <>
      <h1>Audit search</h1>
      <p className="subtitle">Search every moderation action across this server. Combine filters to narrow down.</p>

      <div className="card">
        <form onSubmit={submit}>
          <div className="row">
            <div className="field">
              <label>Target user ID</label>
              <input value={filters.target} onChange={e => update('target', e.target.value)} placeholder="who was affected" />
            </div>
            <div className="field">
              <label>Moderator user ID</label>
              <input value={filters.moderator} onChange={e => update('moderator', e.target.value)} placeholder="who did it" />
            </div>
          </div>
          <div className="row">
            <div className="field">
              <label>Action</label>
              <select value={filters.action} onChange={e => update('action', e.target.value)}>
                <option value="">— any —</option>
                {actions.map(a => <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Source</label>
              <select value={filters.source} onChange={e => update('source', e.target.value)}>
                <option value="">— any —</option>
                <option value="bot">Slash command</option>
                <option value="web">Dashboard</option>
                <option value="cmod">Channel mod</option>
              </select>
            </div>
          </div>
          <div className="field">
            <label>Reason contains</label>
            <input value={filters.q} onChange={e => update('q', e.target.value)} placeholder="any text to match in the reason" />
          </div>
          <div className="row">
            <div className="field">
              <label>From date</label>
              <input type="date" value={filters.since} onChange={e => update('since', e.target.value)} />
            </div>
            <div className="field">
              <label>To date</label>
              <input type="date" value={filters.until} onChange={e => update('until', e.target.value)} />
            </div>
          </div>
          <div className="row actions" style={{ gap: '.5rem' }}>
            {anyFilter && <button type="button" className="ghost" onClick={reset}><RotateCcw size={14} strokeWidth={2.5} /> Reset</button>}
            <button type="submit" disabled={loading}><Search size={14} strokeWidth={2.5} /> {loading ? 'Searching…' : 'Search'}</button>
          </div>
        </form>
      </div>

      <h3>Results {results && <span className="dim" style={{ fontWeight: 400, fontSize: 14 }}>· {results.length}{truncated ? '+' : ''} matched</span>}</h3>

      {!results ? (
        <div className="card"><div className="skel skel-line med" /><div className="skel skel-line short" /></div>
      ) : !results.length ? (
        <div className="card empty"><div className="ico"><Inbox size={32} strokeWidth={1.5} /></div><p>No results matched.</p></div>
      ) : (
        <>
          {truncated && (
            <div className="card" style={{ background: 'rgba(240,178,50,.08)', borderColor: 'var(--warn)', padding: '.65rem .9rem', marginBottom: '.75rem' }}>
              <span className="badge warn"><AlertTriangle size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />truncated</span>
              <span className="dim" style={{ marginLeft: 8, fontSize: 13 }}>Hit the 200-row cap. Tighten the filters to see older entries.</span>
            </div>
          )}
          <div className="card" style={{ padding: 0 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>Moderator</th>
                  <th>Reason</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {results.map(r => {
                  const cls = ACTION_COLOR[r.action] || '';
                  const label = ACTION_LABELS[r.action] || r.action;
                  return (
                    <tr key={r.id}>
                      <td title={new Date(r.created_at * 1000).toLocaleString()} style={{ whiteSpace: 'nowrap' }}>{timeAgo(r.created_at)}</td>
                      <td>
                        <span className={`badge ${cls}`}>{label}</span>
                        {r.duration_ms && <div className="dim" style={{ fontSize: 11, marginTop: 2 }}>{formatDuration(r.duration_ms)}</div>}
                      </td>
                      <td>{r.user_id === '0' ? <span className="dim">—</span> : <UserChip guildId={guildId} userId={r.user_id} size={20} />}</td>
                      <td><UserChip guildId={guildId} userId={r.moderator_id} size={20} /></td>
                      <td style={{ maxWidth: 360 }}>{r.reason || <span className="dim">—</span>}</td>
                      <td><span className="badge">{r.source || 'bot'}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}
